import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, MessageCircle, Clock, Building, Sparkles, Copy } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Quick Links',
      links: [
        { name: 'Home', href: '/' },
        { name: 'Properties', href: '/properties' },
        { name: 'About Us', href: '/about' },
        { name: 'Contact', href: '/contact' },
      ]
    },
    {
      title: 'Our Services',
      links: [
        { name: 'Property Sales', href: '/properties' },
        { name: 'Property Management', href: '/contact' },
        { name: 'Investment Advisory', href: '/contact' },
        { name: 'Legal Documentation', href: '/contact' },
      ]
    },
    {
      title: 'Contact Us',
      links: [
        { name: 'Ali Raza Shah', href: 'tel:+923335997663', icon: Phone, whatsapp: 'https://wa.me/923335997663' },
        { name: 'Ali Murtaza', href: 'tel:+923328563429', icon: Phone, whatsapp: 'https://wa.me/923328563429' },
        { name: 'M Ismail', href: 'tel:+923344933389', icon: Phone, whatsapp: 'https://wa.me/923344933389' },
        { name: 'Yaseen Shah', href: 'tel:+923343362628', icon: Phone, whatsapp: 'https://wa.me/923343362628' },
        { name: 'Telephone', href: 'tel:0514500376', icon: Phone },
      ]
    },
    {
      title: 'Office Info',
      links: [
        { name: 'ismailproperty736@gmail.com', href: 'mailto:ismailproperty736@gmail.com', icon: Mail },
        { name: 'Office No. 42, Ittehad Business Centre', href: '#', icon: Building },
        { name: 'A-Block, Faisal Hills', href: '#', icon: MapPin },
        { name: '24/7 Available', href: '#', icon: Clock },
        { name: 'Faisal Town', href: '#', icon: Building, logo: '/Faisaltown Logo-01-01.png' },
        { name: 'Suffa', href: '#', icon: Building, logo: '/Suffa.png' }
      ]
    }
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo Section */}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="flex items-center space-x-3 group inline-block">
              <div className="relative">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-neon-blue to-primary-600 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
                <img 
                  src="/logo.png" 
                  alt="Ismail Property Advisor" 
                  className="relative h-28 w-28 object-contain group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div>
                <div className="text-xl font-bold text-white group-hover:text-neon-blue transition-colors duration-300">
                  Ismail Property
                </div>
                <div className="flex items-center">
                  <Sparkles className="h-3 w-3 text-neon-blue mr-1" />
                  <span className="text-xs text-gray-400">Advisor</span>
                </div>
              </div>
            </Link>
            
            <p className="text-gray-400 text-sm leading-relaxed">
              Your trusted partner in finding the perfect property. We specialize in residential and commercial real estate across Faisal Hills and Faisal Town.
            </p>

            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl flex items-center justify-center hover:bg-neon-blue/20 hover:border-neon-blue/50 transition-all duration-300 group"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Icon className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Footer Sections */}
          {footerSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: sectionIndex * 0.1 }}
            >
              <h3 className="text-lg font-semibold text-white">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={link.name} className="flex flex-col space-y-1">
                    <div className="flex items-center justify-between">
                      <motion.a
                        href={link.href}
                        className="flex items-center space-x-2 text-gray-400 hover:text-neon-blue transition-colors duration-300 group"
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        {'logo' in link ? (
                          <img 
                            src={link.logo} 
                            alt={link.name}
                            className="h-6 w-6 object-contain flex-shrink-0"
                          />
                        ) : (
                          'icon' in link && link.icon && (
                            <link.icon className="h-4 w-4 flex-shrink-0" />
                          )
                        )}
                        <span className="text-sm font-medium">{link.name}</span>
                      </motion.a>
                      {'whatsapp' in link && (
                        <motion.a
                          href={link.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300 transition-colors duration-300"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </motion.a>
                      )}
                    </div>
                    {section.title === 'Contact Us' && link.href.startsWith('tel:') && (
                      <span className="text-xs text-gray-500 ml-6">
                        {link.href.replace('tel:+92', '+92-').replace('tel:051', '051-')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom Section */}
        <motion.div 
          className="mt-12 pt-8 border-t border-white/10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © {currentYear} Ismail Property Advisor. All rights reserved.
              </p>
            </div>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-neon-blue text-sm transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-neon-blue text-sm transition-colors duration-300">
                Terms of Service
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Glow Effect */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-neon-blue via-primary-600 to-neon-blue opacity-50" />
    </footer>
  );
};

export default Footer;
